import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/** Promoter — edit a still-pending sale. */
export default function PromoterSaleEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [vatRate, setVatRate] = useState(0.13);

  useEffect(() => {
    api.get(`/promoter/sales/${id}`)
      .then(({ data }) => {
        if (data.status !== "pending_verification") {
          toast.error("This sale is already verified — read-only");
        }
        setForm(data);
      })
      .catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }));
    api.get("/promoter/settings/vat").then((r) => setVatRate(Number(r.data?.vat_rate) || 0.13)).catch(() => {});
  }, [id]);

  if (!form) return <div className="text-sm text-slate-500" data-testid="sale-edit-loading">Loading…</div>;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/promoter/sales/${id}`, {
        sale_date: form.sale_date, customer_name: form.customer_name, customer_phone: form.customer_phone,
        customer_address: form.customer_address, brand: form.brand, product_category: form.product_category,
        product_model: form.product_model, product_serial_number: form.product_serial_number,
        quantity: Number(form.quantity || 1),
        invoice_number: form.invoice_number, invoice_amount: Number(form.invoice_amount || 0),
        invoice_date: form.invoice_date, remarks: form.remarks,
      });
      toast.success("Sale updated");
      navigate("/promoter/my-sales");
    } catch (err) {
      toast.error("Could not update", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const readonly = form.status !== "pending_verification";
  const grossAmount = Number(form.invoice_amount || 0);
  const netAmount = grossAmount > 0 ? grossAmount / (1 + vatRate) : 0;
  const vatAmount = grossAmount - netAmount;
  const fmt = (n) => (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const vatPct = (vatRate * 100).toFixed(vatRate * 100 % 1 === 0 ? 0 : 2);

  return (
    <form onSubmit={save} className="space-y-3" data-testid="sale-edit-form">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Edit sale</h1>
        <p className="text-xs text-slate-500 mt-0.5 font-mono">{form.invoice_number}</p>
      </header>

      <Card>
        <CardContent className="p-4 space-y-3">
          <F label="Customer name" required>
            <Input value={form.customer_name || ""} disabled={readonly}
                   onChange={(e) => update("customer_name", e.target.value)} data-testid="edit-customer-name" />
          </F>
          <F label="Customer phone" required>
            <Input value={form.customer_phone || ""} disabled={readonly}
                   onChange={(e) => update("customer_phone", e.target.value)} data-testid="edit-customer-phone" />
          </F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Brand"><Input value={form.brand || ""} disabled={readonly} onChange={(e) => update("brand", e.target.value)} /></F>
            <F label="Category"><Input value={form.product_category || ""} disabled={readonly} onChange={(e) => update("product_category", e.target.value)} /></F>
          </div>
          <F label="Model"><Input value={form.product_model || ""} disabled={readonly} onChange={(e) => update("product_model", e.target.value)} /></F>
          <F label="Serial #"><Input value={form.product_serial_number || ""} disabled={readonly} onChange={(e) => update("product_serial_number", e.target.value)} /></F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Quantity"><Input type="number" min={1} value={form.quantity || 1} disabled={readonly} onChange={(e) => update("quantity", e.target.value)} /></F>
            <F label={`Invoice Total (incl. ${vatPct}% VAT)`}><Input type="number" value={form.invoice_amount || 0} disabled={readonly} onChange={(e) => update("invoice_amount", e.target.value)} data-testid="edit-amount" /></F>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs space-y-0.5"
               data-testid="edit-vat-preview">
            <div className="flex justify-between text-slate-600">
              <span>Invoice gross (incl. VAT)</span>
              <span className="font-mono">Rs. {fmt(grossAmount)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>VAT @ {vatPct}%</span>
              <span className="font-mono" data-testid="edit-vat-amount">Rs. {fmt(vatAmount)}</span>
            </div>
            <div className="flex justify-between font-semibold text-slate-900 pt-0.5 border-t border-slate-200 mt-0.5">
              <span>Net sales (excl. VAT)</span>
              <span className="font-mono" data-testid="edit-net-amount">Rs. {fmt(netAmount)}</span>
            </div>
          </div>
          <F label="Invoice date"><Input type="date" value={form.invoice_date || ""} disabled={readonly} onChange={(e) => update("invoice_date", e.target.value)} /></F>
          <F label="Invoice #"><Input value={form.invoice_number || ""} disabled={readonly} onChange={(e) => update("invoice_number", e.target.value)} /></F>
          <F label="Remarks"><Textarea rows={2} value={form.remarks || ""} disabled={readonly} onChange={(e) => update("remarks", e.target.value)} /></F>
        </CardContent>
      </Card>

      {!readonly && (
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/promoter/my-sales")} className="flex-1" data-testid="edit-cancel-btn">Cancel</Button>
          <Button type="submit" disabled={busy} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid="edit-save-btn">{busy ? "Saving..." : "Save"}</Button>
        </div>
      )}
    </form>
  );
}

function F({ label, required, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase font-semibold text-slate-600">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
